/**
 * Tab showing subscription state.  
| Account state | Status chip | Banner / primary message | Detail labels |
|---|---|---|---|
| **No subscription** | — | **Select A Plan To Start Your 7-Day Free Trial**<br>Cancel anytime. You will only be billed at the end of the free trial. | Plan cards + Subscribe |
| **Complimentary (exempt)** | — | **Complimentary access**<br>Your account has complimentary access to AttorneyAide. No subscription plan is required. | CTA: Go to My Cases |
| **Trialing — will convert to paid** | Free Trial | Your free trial ends on {date}. Your card will be charged automatically after the trial. | Trial ends: {trial_end}<br>Monthly rate |
| **Trialing — pending cancel** | Free Trial + Cancels {date} | Your free trial ends on {date}. Your subscription will cancel then and **you will not be charged**. | Access ends: {cancel_at or trial_end}<br>Monthly rate (for reference) |
| **Active — renewing** | Active | *(no status banner)* | Next renewal: {period_end}<br>Monthly rate |
| **Active — pending cancel** | Active + Cancels {date} | Your subscription will cancel on {date}. You keep access until then and **will not be charged** for the next period. | Access ends: {cancel_at or period_end}<br>Monthly rate |
| **Past due** | Payment Past Due | Your last payment failed. Please update your payment method to avoid service interruption. | CTA: Update Payment Method |
| **Unpaid** | Unpaid | Your subscription is unpaid. Update your payment method or resubscribe to restore full access. | CTA: Update Payment Method / see plans |
| **Paused** | Paused | Your subscription is paused. Resume billing from Manage Subscription to restore full access. | CTA: Manage Subscription |
| **Incomplete** | Incomplete | Your subscription setup is incomplete. Finish checkout or choose a plan to continue. | CTA: Subscribe / Manage |
| **Incomplete expired** | — | Treat like **No subscription** / ended: show plan selection. | Plan cards |
| **Canceled / ended** | — | **Your subscription has ended**<br>Select a plan to continue using AttorneyAide. (You can still view your existing cases.) | Then plan selection |
| **Checkout just completed** (`?session_id=`) | — | **You're all set!**<br>Your subscription is active. You can start processing documents right away. | Transient success |
 */
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StarIcon from '@mui/icons-material/Star';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    LinearProgress,
    Paper,
    Typography
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './App.css';
import { useAuth } from './AuthContext';
import Footer from './Footer';
import LawAppBar, { ACCOUNT_DISABLED_MESSAGE } from './LawAppBar';
import LoginScreen from './LoginScreen';
import {
    handleGoogleLoginSuccess,
    renderGoogleLoginButton,
} from './LoginUtils';

const goldButtonSx = {
    background: 'var(--button-gradient)',
    color: '#fff',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '1.05rem',
    padding: '15px 28px',
    borderRadius: '16px',
    border: 'none',
    boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 30px rgba(212, 175, 55, 0.6)',
        background: 'var(--button-gradient-hover)',
    },
    '&:active': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
        transition: 'left 0.6s',
    },
    '&:hover::before': {
        left: '100%',
    },
};

const outlineButtonSx = {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.9rem',
    padding: '8px 20px',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    color: 'var(--text-primary)',
    '&:hover': {
        background: 'var(--surface-color)',
        borderColor: 'var(--text-secondary)',
    },
};

const planCardSx = {
    padding: { xs: '28px', sm: '32px' },
    borderRadius: '8px',
    boxShadow: 'var(--shadow-light)',
    border: '1px solid var(--border-color)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
};

export default function Account() {
    const {
        isAuthenticated,
        isLoading: authLoading,
        user,
        setAuthenticatedUser,
        logout,
        refreshAuth,
    } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [billingStatus, setBillingStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [checkoutLoading, setCheckoutLoading] = useState(null);
    const [planChangeLoading, setPlanChangeLoading] = useState(false);

    const sessionId = searchParams.get('session_id');

    function onGoogleLoginSuccess(response) {
        handleGoogleLoginSuccess(response, onLoginComplete).catch(() => doLogout());
    }

    function onEmailLoginSuccess(user) {
        onLoginComplete(user);
    }

    function onLoginComplete(user) {
        if (user) setAuthenticatedUser(user);
    }

    function renderLoginButton() {
        renderGoogleLoginButton("googleLogin", onGoogleLoginSuccess);
    }

    async function doLogout() {
        await logout(renderLoginButton);
        navigate('/mycases');
    }

    const fetchBillingStatus = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const res = await fetch('/api/billing/status', { credentials: 'same-origin' });
            if (res.status === 401) {
                doLogout();
                return;
            }
            if (!res.ok) throw new Error('Failed to load billing status');
            const data = await res.json();
            setBillingStatus(data);
            if (
                data.subscriptionExempt
                || data.subscription?.status === 'active'
                || data.subscription?.status === 'trialing'
            ) {
                refreshAuth();
            }
        } catch (err) {
            console.error('Failed to fetch billing status:', err);
            setError('Unable to load your billing information. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isAuthenticated) {
            fetchBillingStatus();
        }
    }, [isAuthenticated, fetchBillingStatus]);

    useEffect(() => {
        if (sessionId && isAuthenticated) {
            const timer = setTimeout(() => {
                setSearchParams({}, { replace: true });
                fetchBillingStatus();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [sessionId, isAuthenticated, fetchBillingStatus, setSearchParams]);

    async function handleSubscribe(planId) {
        try {
            setCheckoutLoading(planId);
            setError(null);
            const res = await fetch('/api/billing/checkout-session', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ planId }),
            });
            if (res.status === 401) {
                doLogout();
                return;
            }
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to start checkout');
            }
            const { url } = await res.json();
            window.location.href = url;
        } catch (err) {
            console.error('Checkout error:', err);
            setError(err.message);
        } finally {
            setCheckoutLoading(null);
        }
    }

    async function handleManageSubscription() {
        try {
            setError(null);
            const res = await fetch('/api/billing/portal', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
            });
            if (res.status === 401) {
                doLogout();
                return;
            }
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to open billing portal');
            }
            const { url } = await res.json();
            window.location.href = url;
        } catch (err) {
            console.error('Portal error:', err);
            setError(err.message);
        }
    }

    async function handleChangePlan(planId) {
        try {
            setPlanChangeLoading(true);
            setError(null);
            const res = await fetch('/api/billing/change-plan', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ planId }),
            });
            if (res.status === 401) {
                doLogout();
                return;
            }
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to change plan');
            }
            const data = await res.json();
            setBillingStatus(data);
            refreshAuth();
        } catch (err) {
            console.error('Change plan error:', err);
            setError(err.message);
        } finally {
            setPlanChangeLoading(false);
        }
    }

    if (authLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--background-color)' }}>
                <img src="/loading.gif" alt="loading..." style={{ height: '48px' }} />
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <LoginScreen
                onGoogleLoginSuccess={onGoogleLoginSuccess}
                onEmailLoginSuccess={onEmailLoginSuccess}
            />
        );
    }

    const sub = billingStatus?.subscription;
    const accountDisabled =
        user?.accountDisabled === true || billingStatus?.accountDisabled === true;
    const subscriptionExempt =
        user?.subscriptionExempt === true || billingStatus?.subscriptionExempt === true;
    const paidActive =
        sub?.status === 'active' || sub?.status === 'trialing';

    return (
        <div className="App">
            <LawAppBar />
            <Container
                maxWidth="md"
                sx={{
                    padding: { xs: '20px', sm: '40px' },
                    marginTop: '40px',
                    marginBottom: '60px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
                    <AccountCircleIcon sx={{ fontSize: { xs: '2rem', sm: '2.5rem' }, color: 'var(--accent-color)' }} />
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.6rem', sm: '2rem' },
                            letterSpacing: '-0.01em',
                        }}
                    >
                        Account & Billing
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ marginBottom: '24px' }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                        <img src="/loading.gif" alt="loading..." style={{ height: '48px' }} />
                    </Box>
                ) : accountDisabled ? (
                    <DisabledView onContact={() => navigate('/contact')} />
                ) : sessionId ? (
                    <SuccessView />
                ) : paidActive ? (
                    <ActiveView
                        sub={sub}
                        onManage={handleManageSubscription}
                        onChangePlan={handleChangePlan}
                        planChangeLoading={planChangeLoading}
                    />
                ) : subscriptionExempt ? (
                    <ExemptView onContinue={() => navigate('/mycases')} />
                ) : sub?.status === 'past_due' ? (
                    <PastDueView sub={sub} onManage={handleManageSubscription} />
                ) : sub?.status === 'unpaid' ? (
                    <UnpaidView sub={sub} onManage={handleManageSubscription} />
                ) : sub?.status === 'paused' ? (
                    <PausedView sub={sub} onManage={handleManageSubscription} />
                ) : sub?.status === 'incomplete' ? (
                    <IncompleteView onManage={handleManageSubscription} onSubscribe={handleSubscribe} checkoutLoading={checkoutLoading} />
                ) : sub?.status === 'canceled' || sub?.status === 'incomplete_expired' ? (
                    <CanceledView
                        checkoutLoading={checkoutLoading}
                        onSubscribe={handleSubscribe}
                    />
                ) : !sub ? (
                    <PlanSelectionView
                        checkoutLoading={checkoutLoading}
                        onSubscribe={handleSubscribe}
                    />
                ) : (
                    <CanceledView
                        checkoutLoading={checkoutLoading}
                        onSubscribe={handleSubscribe}
                    />
                )}
            </Container>
            <Footer />
        </div>
    );
}

function DisabledView({ onContact }) {
    return (
        <Paper elevation={0} sx={{ ...planCardSx, textAlign: 'center', padding: { xs: '40px', sm: '60px' } }}>
            <WarningAmberIcon sx={{ fontSize: '4rem', color: 'var(--error-color)', margin: '0 auto 16px' }} />
            <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '12px' }}>
                Account Disabled
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '28px' }}>
                {ACCOUNT_DISABLED_MESSAGE}.
            </Typography>
            <Button variant="contained" disableElevation sx={goldButtonSx} onClick={onContact}>
                Contact Us
            </Button>
        </Paper>
    );
}

function SuccessView() {
    return (
        <Paper elevation={0} sx={{ ...planCardSx, textAlign: 'center', padding: { xs: '40px', sm: '60px' } }}>
            <CheckCircleOutlineIcon sx={{ fontSize: '4rem', color: '#4caf50', margin: '0 auto 16px' }} />
            <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '12px' }}>
                You're all set!
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                Your subscription is active. You can start processing documents right away.
            </Typography>
        </Paper>
    );
}

function ExemptView({ onContinue }) {
    return (
        <Paper elevation={0} sx={{ ...planCardSx, textAlign: 'center', padding: { xs: '40px', sm: '60px' } }}>
            <CheckCircleOutlineIcon sx={{ fontSize: '4rem', color: '#4caf50', margin: '0 auto 16px' }} />
            <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '12px' }}>
                Complimentary access
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '28px' }}>
                Your account has complimentary access to AttorneyAide. No subscription plan is required.
            </Typography>
            <Button variant="contained" disableElevation sx={goldButtonSx} onClick={onContinue}>
                Go to My Cases
            </Button>
        </Paper>
    );
}

function PlanSelectionView({ checkoutLoading, onSubscribe }) {
    return (
        <>
            <Paper elevation={0} sx={{ ...planCardSx, textAlign: 'center', marginBottom: '32px', padding: { xs: '28px', sm: '40px' } }}>
                <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }}>
                    Select A Plan To Start Your 7-Day Free Trial
                </Typography>
                <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                    Cancel anytime. You will only be billed at the end of the free trial.
                </Typography>
            </Paper>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, marginBottom: '32px' }}>
                {/* Basic */}
                <Paper elevation={0} sx={planCardSx}>
                    <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1.35rem', marginBottom: '12px' }}>
                        Basic
                    </Typography>
                    <Typography variant="h4" sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '16px' }}>
                        $95
                        <Typography component="span" variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 400 }}>
                            {' '}per month
                        </Typography>
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontSize: '0.875rem', fontWeight: 500, marginBottom: '8px' }}>
                        1,000 pages included, 10¢/page overage
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '16px', lineHeight: 1.6 }}>
                        Full feature access. Best for occasional use.
                    </Typography>
                    <Box sx={{ marginTop: 'auto', paddingTop: '16px' }}>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={checkoutLoading === 'basic'}
                            onClick={() => onSubscribe('basic')}
                            sx={goldButtonSx}
                        >
                            {checkoutLoading === 'basic' ? 'Redirecting...' : 'Subscribe to Basic'}
                        </Button>
                    </Box>
                </Paper>

                {/* Professional */}
                <Paper elevation={0} sx={{ ...planCardSx, border: '2px solid var(--accent-color)', position: 'relative' }}>
                    <Chip
                        icon={<StarIcon sx={{ fontSize: '1rem !important' }} />}
                        label="Most popular"
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: -10,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'var(--button-gradient)',
                            color: '#1a1a1a',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: '#1a1a1a' },
                        }}
                    />
                    <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1.35rem', marginBottom: '12px', marginTop: '8px' }}>
                        Professional
                    </Typography>
                    <Typography variant="h4" sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '16px' }}>
                        $195
                        <Typography component="span" variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 400 }}>
                            {' '}per month
                        </Typography>
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontSize: '0.875rem', fontWeight: 500, marginBottom: '8px' }}>
                        3,000 pages included, 5¢/page overage
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '16px', lineHeight: 1.6 }}>
                        Higher capacity for active practices. Full feature access.
                    </Typography>
                    <Box sx={{ marginTop: 'auto', paddingTop: '16px' }}>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={checkoutLoading === 'professional'}
                            onClick={() => onSubscribe('professional')}
                            sx={goldButtonSx}
                        >
                            {checkoutLoading === 'professional' ? 'Redirecting...' : 'Subscribe to Professional'}
                        </Button>
                    </Box>
                </Paper>
            </Box>

            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textAlign: 'center', fontSize: '0.85rem' }}>
                All plans are month-to-month. Any changes or cancellations apply to the next billing cycle.
            </Typography>
        </>
    );
}

function formatSubDate(ms) {
    return ms ? new Date(ms).toLocaleDateString() : null;
}

function isPendingCancel(sub) {
    return Boolean(sub?.cancelAtPeriodEnd || sub?.cancelAt);
}

function ActiveView({ sub, onManage, onChangePlan, planChangeLoading }) {
    const isTrial = sub.status === 'trialing';
    const pendingCancel = isPendingCancel(sub);
    const pagesPercent = sub.pagesIncluded > 0
        ? Math.min(100, Math.round((sub.pagesUsed / sub.pagesIncluded) * 100))
        : 0;
    const planLabel = sub.plan === 'professional' ? 'Professional' : 'Basic';
    const periodEnd = formatSubDate(sub.currentPeriodEnd) || 'N/A';
    const trialEnd = formatSubDate(sub.trialEnd);
    const cancelDate = formatSubDate(sub.cancelAt) || (isTrial ? trialEnd : periodEnd);
    const accessEndDate = formatSubDate(sub.cancelAt) || (isTrial ? trialEnd : periodEnd) || periodEnd;
    const canChangePlan = !pendingCancel && typeof onChangePlan === 'function';

    let statusBanner = null;
    if (isTrial && pendingCancel && accessEndDate) {
        statusBanner = (
            <Alert severity="info" sx={{ marginBottom: '20px' }}>
                Your free trial ends on {accessEndDate}. Your subscription will cancel then and you will not be charged.
            </Alert>
        );
    } else if (isTrial && trialEnd && !pendingCancel) {
        statusBanner = (
            <Alert severity="info" sx={{ marginBottom: '20px' }}>
                Your free trial ends on {trialEnd}. Your card will be charged automatically after the trial.
            </Alert>
        );
    } else if (!isTrial && pendingCancel && cancelDate) {
        statusBanner = (
            <Alert severity="info" sx={{ marginBottom: '20px' }}>
                Your subscription will cancel on {cancelDate}. You keep access until then and will not be charged for the next period.
            </Alert>
        );
    }

    let detailLabel = 'Next renewal';
    let detailDate = periodEnd;
    if (pendingCancel) {
        detailLabel = 'Access ends';
        detailDate = accessEndDate || periodEnd;
    } else if (isTrial) {
        detailLabel = 'Trial ends';
        detailDate = trialEnd || periodEnd;
    }

    return (
        <Paper elevation={0} sx={{ ...planCardSx, padding: { xs: '28px', sm: '40px' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, marginBottom: '24px' }}>
                <Box>
                    <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '4px' }}>
                        {planLabel} Plan
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                            size="small"
                            label={isTrial ? 'Free Trial' : 'Active'}
                            sx={{
                                background: isTrial ? '#e3f2fd' : '#e8f5e9',
                                color: isTrial ? '#1565c0' : '#2e7d32',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                            }}
                        />
                        {pendingCancel && cancelDate && (
                            <Chip
                                size="small"
                                label={`Cancels ${cancelDate}`}
                                sx={{
                                    background: '#fff3e0',
                                    color: '#e65100',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                }}
                            />
                        )}
                    </Box>
                </Box>
                <Button variant="outlined" onClick={onManage} sx={outlineButtonSx}>
                    Manage Subscription
                </Button>
            </Box>

            {statusBanner}

            <Box sx={{ marginBottom: '24px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                        Pages used this period
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                        {sub.pagesUsed.toLocaleString()} / {sub.pagesIncluded.toLocaleString()}
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={pagesPercent}
                    sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: 'var(--surface-color)',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            background: sub.pagesUsed > sub.pagesIncluded
                                ? '#ef5350'
                                : pagesPercent >= 80
                                    ? '#ff9800'
                                    : 'var(--button-gradient)',
                        },
                    }}
                />
                {sub.pagesUsed > sub.pagesIncluded && (
                    <Typography variant="caption" sx={{ color: '#ef5350', marginTop: '4px', display: 'block' }}>
                        You've exceeded your included pages. Overage will appear on your next invoice.
                    </Typography>
                )}
            </Box>

            {canChangePlan && sub.plan === 'basic' && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        padding: '14px 16px',
                        marginBottom: '24px',
                        borderRadius: '8px',
                        border: '1px solid var(--accent-color)',
                        background: 'rgba(212, 175, 55, 0.04)',
                        textAlign: 'left',
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: '4px' }}>
                            Need more capacity?
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                            Upgrade to Professional for 3,000 pages at 5¢/page overage. (Changes will take effect at the start of the next billing cycle)
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        disableElevation
                        disabled={planChangeLoading}
                        onClick={() => onChangePlan('professional')}
                        sx={{
                            ...goldButtonSx,
                            fontSize: '0.9rem',
                            padding: '10px 22px',
                            borderRadius: '12px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                        }}
                    >
                        {planChangeLoading ? 'Updating...' : 'Upgrade'}
                    </Button>
                </Box>
            )}

            {canChangePlan && sub.plan === 'professional' && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        padding: '14px 16px',
                        marginBottom: '24px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--surface-color)',
                        textAlign: 'left',
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: '4px' }}>
                            Not using your full capacity?
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                            Switch to Basic to lower your monthly cost to $95/mo for 1,000 pages with 10¢/page overage. (Changes will take effect at the start of the next billing cycle)
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        disabled={planChangeLoading}
                        onClick={() => onChangePlan('basic')}
                        sx={{ ...outlineButtonSx, whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                        {planChangeLoading ? 'Updating...' : 'Switch to Basic'}
                    </Button>
                </Box>
            )}

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
                padding: '16px',
                background: 'var(--surface-color)',
                borderRadius: '8px',
            }}>
                <Box>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '2px' }}>
                        {detailLabel}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                        {detailDate}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '2px' }}>
                        Monthly rate
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                        ${sub.plan === 'professional' ? '195' : '95'}/mo
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
}

function PastDueView({ sub, onManage }) {
    return (
        <Paper elevation={0} sx={{ ...planCardSx, padding: { xs: '28px', sm: '40px' } }}>
            <Alert
                severity="warning"
                icon={<WarningAmberIcon />}
                sx={{ marginBottom: '24px' }}
            >
                Your last payment failed. Please update your payment method to avoid service interruption.
            </Alert>

            <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }}>
                {sub.plan === 'professional' ? 'Professional' : 'Basic'} Plan
            </Typography>
            <Chip
                size="small"
                label="Payment Past Due"
                sx={{
                    background: '#fff3e0',
                    color: '#e65100',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    marginBottom: '24px',
                }}
            />

            <Button
                variant="contained"
                startIcon={<CreditCardIcon />}
                onClick={onManage}
                sx={{ ...goldButtonSx, width: 'fit-content' }}
            >
                Update Payment Method
            </Button>
        </Paper>
    );
}

function UnpaidView({ sub, onManage }) {
    return (
        <Paper elevation={0} sx={{ ...planCardSx, padding: { xs: '28px', sm: '40px' } }}>
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ marginBottom: '24px' }}>
                Your subscription is unpaid. Update your payment method or resubscribe to restore full access.
            </Alert>

            <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }}>
                {sub.plan === 'professional' ? 'Professional' : 'Basic'} Plan
            </Typography>
            <Chip
                size="small"
                label="Unpaid"
                sx={{
                    background: '#fff3e0',
                    color: '#e65100',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    marginBottom: '24px',
                }}
            />

            <Button
                variant="contained"
                startIcon={<CreditCardIcon />}
                onClick={onManage}
                sx={{ ...goldButtonSx, width: 'fit-content' }}
            >
                Update Payment Method
            </Button>
        </Paper>
    );
}

function PausedView({ sub, onManage }) {
    return (
        <Paper elevation={0} sx={{ ...planCardSx, padding: { xs: '28px', sm: '40px' } }}>
            <Alert severity="info" sx={{ marginBottom: '24px' }}>
                Your subscription is paused. Resume billing from Manage Subscription to restore full access.
            </Alert>

            <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }}>
                {sub.plan === 'professional' ? 'Professional' : 'Basic'} Plan
            </Typography>
            <Chip
                size="small"
                label="Paused"
                sx={{
                    background: '#e3f2fd',
                    color: '#1565c0',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    marginBottom: '24px',
                }}
            />

            <Button variant="outlined" onClick={onManage} sx={outlineButtonSx}>
                Manage Subscription
            </Button>
        </Paper>
    );
}

function IncompleteView({ onManage, onSubscribe, checkoutLoading }) {
    return (
        <>
            <Paper elevation={0} sx={{ ...planCardSx, padding: { xs: '28px', sm: '40px' }, marginBottom: '32px' }}>
                <Alert severity="info" sx={{ marginBottom: '24px' }}>
                    Your subscription setup is incomplete. Finish checkout or choose a plan to continue.
                </Alert>

                <Chip
                    size="small"
                    label="Incomplete"
                    sx={{
                        background: '#e3f2fd',
                        color: '#1565c0',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        marginBottom: '24px',
                    }}
                />

                <Button variant="outlined" onClick={onManage} sx={outlineButtonSx}>
                    Manage Subscription
                </Button>
            </Paper>

            <PlanSelectionView
                checkoutLoading={checkoutLoading}
                onSubscribe={onSubscribe}
            />
        </>
    );
}

function CanceledView({ checkoutLoading, onSubscribe }) {
    return (
        <>
            <Paper elevation={0} sx={{ ...planCardSx, textAlign: 'center', marginBottom: '32px', padding: { xs: '28px', sm: '40px' } }}>
                <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }}>
                    Your subscription has ended
                </Typography>
                <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                    Select a plan to continue using AttorneyAide. (You can still view your existing cases.)
                </Typography>
            </Paper>

            <PlanSelectionView
                checkoutLoading={checkoutLoading}
                onSubscribe={onSubscribe}
            />
        </>
    );
}
